import { Fragment, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import OrgIcon from "@mui/icons-material/Business";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InviteIcon from "@mui/icons-material/ForwardToInbox";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import UserIcon from "@mui/icons-material/SettingsAccessibility";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Auth } from "aws-amplify";

import constants from "../../assets/constants";
import * as AdminSessionActions from "../../state/redux/adminSession";
import * as CreationActions from "../../state/redux/creatorSession";
import * as PartnerSessionActions from "../../state/redux/partnerSession";
import * as ProviderSessionActions from "../../state/redux/providerSession";
import * as UserSessionActions from "../../state/redux/userSession";
import { deepCopyObject } from "../../utils/helpers";
import OrganizationDialog from "../dialogs/organizationDialog";
import UserDialog from "../dialogs/userDialog";
import { getCurrentPortalSection, PRIMARY_PORTAL_SECTIONS } from "./portalSections";

// Width of the rail when collapsed (icons only) and when expanded (icons + labels).
export const NAV_DRAWER_COLLAPSED_WIDTH = 64;
export const NAV_DRAWER_EXPANDED_WIDTH = 240;
const APP_BAR_HEIGHT = 64; // keep in sync with portalAppBar / portalGridContent

const PortalNavDrawer = props => {
  const {
    awsGroup,
    userODM,
    organizationODM,
    testBotODM,
    navDrawerOpen: open,
    setNavDrawerOpen,
  } = props;

  const theme = useTheme();

  // Dialog state for the relocated account/options actions.
  const [editUserODM, setEditUserODM] = useState(null);
  const [editOrganizationODM, setEditOrganizationODM] = useState(null);

  const currentRoute = window.location.pathname;

  if (awsGroup == null) {
    return <Fragment></Fragment>;
  }

  const handleLogout = () => {
    Auth.signOut().then(() => window.location.reload());
    props.setShowSettingsRequest(false);
  };

  // Primary section navigation, built from the shared section definitions. Items
  // with no destination for the current user group are dropped.
  const activeSectionKey = getCurrentPortalSection(currentRoute).key;
  const navItems = PRIMARY_PORTAL_SECTIONS.map(section => ({
    key: section.key,
    label: section.label,
    Icon: section.Icon,
    target: section.targetByGroup[awsGroup],
    active: section.key === activeSectionKey,
    // The Testing silo needs a test bot to operate against — don't let users
    // navigate there if one hasn't been resolved.
    disabled: section.key === "Testing" && testBotODM == null,
  })).filter(item => item.target != null);

  // Account / options — relocated from the (removed) top-right menu. These only
  // appear once the drawer is expanded, so they are never shown in the condensed rail.
  const accountItems = [
    {
      key: "Invites",
      label: "Invites",
      Icon: InviteIcon,
      visible: awsGroup === constants.userGroups.Administrator,
      onClick: () => props.history.push(constants.appPaths.appPathAdminInvite),
    },
    {
      key: "Organization",
      label: "Organization",
      Icon: OrgIcon,
      visible: awsGroup === constants.userGroups.Administrator,
      disabled: organizationODM == null,
      onClick: () => setEditOrganizationODM(organizationODM),
    },
    {
      key: "MyInfo",
      label: "My Info",
      Icon: UserIcon,
      visible: userODM != null,
      disabled: userODM == null,
      onClick: () => setEditUserODM(userODM),
    },
    {
      key: "Settings",
      label: "Settings",
      Icon: SettingsIcon,
      visible: true,
      disabled: props.showSettings,
      onClick: () => props.setShowSettingsRequest(true),
    },
    {
      key: "Logout",
      label: "Logout",
      Icon: LogoutRoundedIcon,
      visible: true,
      disabled: props.showSettings,
      onClick: handleLogout,
    },
  ].filter(item => item.visible);

  const width = open ? NAV_DRAWER_EXPANDED_WIDTH : NAV_DRAWER_COLLAPSED_WIDTH;

  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  });

  return (
    <Fragment>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          transition: widthTransition,
          "& .MuiDrawer-paper": {
            width,
            top: APP_BAR_HEIGHT, // clipped under the app bar
            height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
            overflowX: "hidden",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRight: `1px solid ${theme.palette.primary.light}`,
            zIndex: 997, // below the app bar (998) so it stays clipped
            transition: widthTransition,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            padding: theme.spacing(0.5),
            // Mirror the 4px active-indicator border on the nav items below so the
            // collapsed toggle icon lines up vertically with them.
            borderLeft: "4px solid transparent",
          }}
        >
          {open && (
            <Typography variant="subtitle1" sx={{ marginLeft: theme.spacing(1), fontWeight: 600 }}>
              Menu
            </Typography>
          )}
          <IconButton
            color="inherit"
            aria-label={open ? "Collapse navigation" : "Expand navigation"}
            onClick={() => setNavDrawerOpen(!open)}
            sx={{ marginTop: "2px" }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.16)" }} />

        <List>
          {navItems.map(({ key, label, Icon, target, active, disabled }) => {
            const isActive = active != null ? active : currentRoute === target;
            return (
              <Tooltip
                key={key}
                title={label}
                placement="right"
                disableHoverListener={open}
                disableFocusListener={open}
              >
                {/* span wrapper lets the tooltip work even when the button is disabled */}
                <span>
                  <ListItemButton
                    disabled={disabled}
                    selected={isActive}
                    onClick={() => props.history.push(target)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      borderLeft: `4px solid ${
                        isActive ? theme.palette.primary.contrastText : "transparent"
                      }`,
                      "&.Mui-selected": {
                        backgroundColor: "rgba(255, 255, 255, 0.16)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.24)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        marginRight: open ? theme.spacing(2) : "auto",
                        justifyContent: "center",
                        color: "inherit",
                      }}
                    >
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ fontWeight: isActive ? "bold" : "normal" }}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
        </List>

        {/* Account / options — relocated from the removed top-right dropdown. Always
            rendered (icons collapsed, icons + labels expanded) so they stay accessible. */}
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.16)" }} />
        <List>
          {accountItems.map(({ key, label, Icon, onClick, disabled }) => (
            <Tooltip
              key={key}
              title={label}
              placement="right"
              disableHoverListener={open}
              disableFocusListener={open}
            >
              {/* span wrapper lets the tooltip work even when the button is disabled */}
              <span>
                <ListItemButton
                  disabled={disabled}
                  onClick={onClick}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    borderLeft: "4px solid transparent",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      marginRight: open ? theme.spacing(2) : "auto",
                      justifyContent: "center",
                      color: "inherit",
                    }}
                  >
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={label} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
              </span>
            </Tooltip>
          ))}
        </List>
      </Drawer>

      {editUserODM && organizationODM && (
        <UserDialog
          open={editUserODM != null}
          dialogTitle={"Edit Information"}
          omniODM={deepCopyObject(editUserODM)}
          tabId={
            organizationODM.omniData.organizationType === constants.organizationTypeEnums.nsight
              ? 1
              : 0
          }
          organizationType={organizationODM.omniData.organizationType}
          onAccept={user => {
            if (awsGroup === constants.userGroups.Partner) {
              props.updatePartnerRequest(user);
            } else if (awsGroup === constants.userGroups.Provider) {
              props.updateProviderRequest(user);
            } else if (awsGroup === constants.userGroups.Administrator) {
              props.updateAdministratorRequest(user);
            }
            setEditUserODM(null);
          }}
          onClose={() => setEditUserODM(null)}
        />
      )}

      {editOrganizationODM && (
        <OrganizationDialog
          open={editOrganizationODM != null}
          dialogTitle={"Edit Organization"}
          omniDMO={deepCopyObject(editOrganizationODM)}
          fullWidth={true}
          onAccept={selectedOrganization => {
            props.updateOrganizationRequest(selectedOrganization);
            setEditOrganizationODM(null);
          }}
          onClose={() => setEditOrganizationODM(null)}
        />
      )}
    </Fragment>
  );
};

const mapStateToProps = state => ({
  awsGroup: state.userSession.awsGroup,
  userODM: state.userSession.userODM,
  organizationODM: state.userSession.organizationODM,
  testBotODM: state.botpressSession.testBotODM,
  showSettings: state.userSession.showSettings,
  navDrawerOpen: state.userSession.navDrawerOpen,
});

const mapDispatchToProps = {
  setNavDrawerOpen: UserSessionActions.setNavDrawerOpen,
  setShowSettingsRequest: UserSessionActions.setShowSettingsRequest,
  updateOrganizationRequest: CreationActions.updateOrganizationRequest,
  updatePartnerRequest: PartnerSessionActions.updatePartnerRequest,
  updateProviderRequest: ProviderSessionActions.updateProviderRequest,
  updateAdministratorRequest: AdminSessionActions.updateAdministratorRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PortalNavDrawer));
